import { useHandleResponse } from '@/utils/useHandleResponse';
import { useMutation, useQuery } from '@tanstack/react-query';
import { App, Button, Spin, Tabs } from 'antd';
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { setUser, updateField } from '../../../contextRedux/User/UserModel';
import { setUserRoles } from '../../../contextRedux/User/UserRoleStore';
import { setUserAdministrationCompanies } from '../../../contextRedux/UserAdministrationCompany/UserAdministrationCompanyStore';
import { setUserGroups } from '../../../contextRedux/UserGroup/UserGroupStore';
import AdministrationCompanyService from '../../../services/administrationCompany/AdministrationCompanyService';
import ConfigurationService from '../../../services/configuration/ConfigurationService';
import UserService from '../../../services/user/UserService';
import UserGroupService from '../../../services/userGroup/UserGroupService';
import { RootState } from '../../../store';
import { Tab } from '../../../utils/tabs';
import UserDetailsTab from './Tabs/UserDetailsTab/UserDetailsTab';
import UserSignatureTab from './Tabs/UserSignatureTab/UserSignatureTab';

const NewUser: FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const handleResponse = useHandleResponse();

  const location = useLocation();

  const useQueryParams = () => {
    return new URLSearchParams(useLocation().search);
  };

  const query = useQueryParams();
  const { message } = App.useApp();
  const [id, setId] = useState<string | null>(query.get('id'));

  const reduxUser = useSelector((state: RootState) => state?.models.user);

  const { data: _user, isFetching: isUsersLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () =>
      UserService.search({
        ids: [id!],
        isGroupIncluded: true,
        isPersonalDataIncluded: true,
        isUserAdministrationCompaniesIncluded: true,
      }).then((data) => {
        dispatch(setUser(data?.response[0]));
        return data?.response[0];
      }),
    enabled: !!id,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const {
    data: _userRoles,
    isFetching: isStatusesLoading,
    isError: _isErrorOnFetchStatuses,
    error: _statusesError,
  } = useQuery({
    queryKey: ['searchUserRols'],
    queryFn: () =>
      ConfigurationService.searchUserRols({}).then((data: any) => {
        dispatch(setUserRoles(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const {
    data: _userGroups,
    isFetching: isUserGroupsLoading,
    isError: _isErrorOnFetchUserGroups,
    error: _userGroupsError,
  } = useQuery({
    queryKey: ['userGroups'],
    queryFn: () =>
      UserGroupService.search({ isSupervisorsIncluded: true }).then((data: any) => {
        dispatch(setUserGroups(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const {
    data: _userAdministrationCompanies,
    isFetching: isUserAdministrationCompanies,
    isError: _isErrorOnFetchUserAdministrationCompanies,
    error: _userAdministrationCompaniesError,
  } = useQuery({
    queryKey: ['userAdministrationCompanies'],
    queryFn: () =>
      AdministrationCompanyService.search({}).then((data: any) => {
        dispatch(setUserAdministrationCompanies(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const handleSaveUser = async () => {
    try {
      await onSaveUser();
    } catch (errorInfo: any) {
      message.error(errorInfo?.errorFields[0]?.errors);
    }
  };

  const onSaveUser = async () => {
    saveUser(reduxUser);
  };

  const { mutate: saveUser, isPending: isSavingUser } = useMutation({
    mutationFn: (formData: any) => UserService.upsert(id, formData),
    onSuccess: (data: any) => {
      if (data) {
        handleResponse(data, data?.response?.name);
        data?.response && navigate(`/administration/user/edit?id=${data?.response?.id}`);
      }
    },
    onError: (error: any) => {
      message.error(error.message);
    },
  });

  useEffect(() => {
    const idFromUrl = query.get('id');
    if (!idFromUrl) {
    }
    setId(idFromUrl);
  }, [location.search]);

  useEffect(() => {
    if (id) {
      dispatch(updateField({ field: 'id', value: id }));
    } else {
      dispatch(updateField({ field: 'id', value: '' }));
    }
  }, [id, dispatch]);

  const tabs: Tab[] = [
    {
      key: 'userDetailsTab',
      label: 'Detalii User',
      children: <UserDetailsTab />,
    },
    // {
    //   key: 'userPersonalData',
    //   label: 'Date Personale',
    //   children: <UserPersonalDataTab />,
    // },
    // {
    //   key: 'userCompanyTab',
    //   label: 'Companii',
    //   children: <UserCompanyTab />,
    // },
    {
      key: 'userSignatureTab',
      label: 'Semnatura',
      children: <UserSignatureTab />,
    },
  ];

  if (isUsersLoading || isStatusesLoading || isUserGroupsLoading || isUserAdministrationCompanies || isSavingUser) {
    return <Spin size='large' className='spinner'></Spin>;
  }

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
        }}
      >
        <h2>{id ? 'User - ' + reduxUser?.name : 'User Nou'}</h2>
        <div>
          <Button onClick={() => handleSaveUser()}>Salveaza User</Button>
        </div>
      </div>
      <Tabs defaultActiveKey='1' items={tabs} />
    </>
  );
};

export default NewUser;
